import { message } from 'antd';

import Utils from '../../../../Assets/Scripts/Utils';
import { api } from '../../../../Services/axiosService';

const RESERVED_ROLE_ID = 1;

function readProp(source, camelKey, pascalKey) {
  if (!source) {
    return undefined;
  }

  if (source[camelKey] !== undefined) {
    return source[camelKey];
  }

  return source[pascalKey];
}

function unwrapResource(payload, resourceId) {
  if (Array.isArray(payload)) {
    const numericId = Number(resourceId);
    return payload.find((item) => (item.id ?? item.Id) === numericId) ?? payload[0] ?? {};
  }

  return payload ?? {};
}

function normalizeCatalogField(field) {
  return {
    name: readProp(field, 'name', 'Name'),
    label: readProp(field, 'label', 'Label'),
    fieldType: readProp(field, 'fieldType', 'FieldType') ?? null,
  };
}

function normalizeOverlayField(field) {
  return {
    name: readProp(field, 'name', 'Name'),
    order: readProp(field, 'order', 'Order') ?? 0,
    visibilityMode: readProp(field, 'visibilityMode', 'VisibilityMode'),
    viewerRoleIds: readProp(field, 'viewerRoleIds', 'ViewerRoleIds') ?? [],
    isNew: Boolean(readProp(field, 'isNew', 'IsNew')),
    fieldType: readProp(field, 'fieldType', 'FieldType') ?? null,
  };
}

function normalizeOverlaySection(section) {
  return {
    sectionId: readProp(section, 'sectionId', 'SectionId'),
    name: readProp(section, 'name', 'Name') ?? '',
    order: readProp(section, 'order', 'Order') ?? 0,
    fieldNames: [...(readProp(section, 'fieldNames', 'FieldNames') ?? [])],
  };
}

function normalizeCustomization(payload) {
  const source = payload ?? {};
  return {
    resourceId: readProp(source, 'resourceId', 'ResourceId'),
    fields: (readProp(source, 'fields', 'Fields') ?? []).map(normalizeOverlayField),
    sections: (readProp(source, 'sections', 'Sections') ?? []).map(normalizeOverlaySection),
  };
}

function normalizeRole(role) {
  return {
    id: readProp(role, 'id', 'Id'),
    name: readProp(role, 'name', 'Name'),
    status: readProp(role, 'status', 'Status'),
  };
}

function persistUserAndRole(payload) {
  const userData = payload?.userData;
  const roleData = payload?.roleData;
  if (userData) {
    localStorage.setItem(
      'conecta__userData',
      JSON.stringify({
        ...userData,
        companyId: userData.company?.id,
        companyName: userData.company?.name,
      })
    );
  }

  if (roleData) {
    localStorage.setItem('conecta__permissions', JSON.stringify(roleData));
  }
}

export async function getResourceCatalog(resourceId) {
  try {
    const response = await api.get(`/Resource?id=${resourceId}`);
    const resource = unwrapResource(response.data, resourceId);
    return {
      id: readProp(resource, 'id', 'Id'),
      name: readProp(resource, 'name', 'Name'),
      fields: (readProp(resource, 'fields', 'Fields') ?? []).map(normalizeCatalogField),
    };
  } catch (caught) {
    Utils.logError(caught);
    message.error('Oops. Algo deu errado ao buscar os campos do recurso!');
    throw caught;
  }
}

export async function getCustomization(resourceId) {
  try {
    const response = await api.get(`/ResourceFieldCustomization?resourceId=${resourceId}`);
    return normalizeCustomization(response.data);
  } catch (caught) {
    Utils.logError(caught);
    message.error('Oops. Algo deu errado ao buscar a customização!');
    throw caught;
  }
}

export async function saveCustomization(payload) {
  const response = await api.put('/ResourceFieldCustomization', payload);
  return normalizeCustomization(response.data);
}

export async function getResourceFieldLayout(resourceId) {
  const response = await api.get(`/ResourceFieldCustomization/layout?resourceId=${resourceId}`);
  return response.data;
}

export function emptyFieldLayout() {
  return { state: 'padrao', unsectioned: [], sections: [] };
}

export async function loadAgendaFieldLayout(resourceId) {
  if (!resourceId) {
    return emptyFieldLayout();
  }

  try {
    const layout = await getResourceFieldLayout(resourceId);
    return {
      state: layout?.state ?? layout?.State ?? 'padrao',
      unsectioned: layout?.unsectioned ?? layout?.Unsectioned ?? [],
      sections: layout?.sections ?? layout?.Sections ?? [],
    };
  } catch (caught) {
    Utils.logError(caught);
    return emptyFieldLayout();
  }
}

export async function getActiveRoles() {
  try {
    const response = await api.get('/Role');
    const roles = Array.isArray(response.data) ? response.data : [];
    return roles
      .map(normalizeRole)
      .filter((role) => role.id !== RESERVED_ROLE_ID && role.status === 'Ativo');
  } catch (caught) {
    Utils.logError(caught);
    message.error('Oops. Algo deu errado ao buscar os cargos!');
    throw caught;
  }
}

export async function reloadUserAndRole() {
  const storedUser = JSON.parse(localStorage.getItem('conecta__userData'));
  if (!storedUser?.id) {
    return;
  }

  const response = await api.get(`/User/UserAndRole?userId=${storedUser.id}`);
  persistUserAndRole(response.data);
}
